import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { RootState } from "app/lib/store/store";
import { useDispatch, useSelector } from "react-redux";
import {
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/personalDetails";
import { useNavigate } from "react-router-dom";
import { usePersonalDetails } from "app/hooks/applicant/usePersonalDetails";
import { APPLICANT } from "app/constants/apiEndPoints";
import toast from "react-hot-toast";

import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import { handleApiError } from "app/utils/handleApiError";

import DynamicFormFive from "./dynamicForms/DynamicFormFive";

import { PersonalFormFive } from "app/utils/types";

const PartFive: React.FC = () => {
  const applicantId = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds.applicantID
  );
  const navigate = useNavigate();

  const { mutate: submitPersonalDetails, isPending } = usePersonalDetails();
  const { formFive, ...others } = useSelector(
    (state: RootState) => state.personalDetails.stepsData
  );

  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds
  );
  const { applicants } = useSelector(
    (state: RootState) => state.loanTypesSteps.stepsData
  );

  const dispatch = useDispatch();
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    register,
    setError,
    watch,
    clearErrors,
  } = useForm<PersonalFormFive>({
    defaultValues: {
      street: "",
      housingStatus: "",
      state: "",
      sameMailingAddress: false,
    },
  });

  const onSubmit = (data: PersonalFormFive) => {
    const {
      street,
      housingStatus,
      state,
      sameMailingAddress,
      monthsOfStay,
      yearsOfStay,
      postCode,
    } = data;
    const body: any = {
      sameMailingAddress,
      yearsOfStay: Number(yearsOfStay),
      monthsOfStay: Number(monthsOfStay),
      housingStatus,
    };

    if (!sameMailingAddress) {
      body.street = street;
      body.state = state;
      body.postCode = String(postCode);
    }

    submitPersonalDetails(
      {
        url: `${APPLICANT}/${applicantId}/fifth`,
        data: body,
      },
      {
        onSuccess: (data: any) => {
          if (savedIds.employmentID) {
            navigate(`/employment-details`);
          } else {
            toast.error("Employment id is not found!");
          }
        },
        onError: (error: any) => {
          handleApiError(error);
        },
      }
    );

    const storeData: any = {
      monthsOfStay,
      yearsOfStay,
      sameMailingAddress,
      housingStatus,
    };
    if (!sameMailingAddress) {
      storeData.street = street;
      storeData.state = state;
      storeData.postCode = postCode;
    }

    dispatch(
      addStepsData({
        ...others,
        formFive: storeData,
      })
    );
  };

  useEffect(() => {
    if (formFive) {
      reset(formFive);
    } else {
      const { address } = applicants || {};
      if (address?.postCode) {
        setValue("postCode", address?.postCode);
      }
      if (address?.street) {
        setValue("street", address?.street);
      }
      if (address?.state) {
        setValue("state", address?.state);
      }
    }
  }, [formFive, reset]);

  return (
    <>
      <ProgressBar progress={100} MB={7} />
      <div className="w-full  sm:w-[70%] xl:w-[40%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Personal Details" />

        <form onSubmit={handleSubmit(onSubmit)}>
          <DynamicFormFive
            control={control}
            errors={errors}
            register={register}
            setValue={setValue}
            setError={setError}
            clearErrors={clearErrors}
            sameMailingAddress="sameMailingAddress"
            residentialAddressValue={watch("sameMailingAddress")}
            housingStatus="housingStatus"
            monthsOfStay="monthsOfStay"
            yearsOfStay="yearsOfStay"
            addressStreetName="street"
            addressStateName="state"
            addressPostCodeName="postCode"
          />
          <div className="flex justify-between my-10">
            <PreviousButtom onClick={() => dispatch(prevStep())}>
              Previous
            </PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PartFive;
